<template>
  <div class="max-w-7xl mx-auto p-4 sm:p-6 md:p-8">
    <header class="mb-8 text-center sm:text-left">
      <h1 class="text-3xl sm:text-4xl font-bold tracking-tight text-slate-800 dark:text-slate-100">
        Meus
        <span class="bg-clip-text text-transparent bg-gradient-to-r from-[#7B2CBF] to-[#A367E1]">Gastos Fixos</span>
      </h1>
      <p class="text-sm text-slate-600 dark:text-slate-400 mt-1">
        Acompanhe, gerencie e analise suas despesas fixas.
      </p>
    </header>

    <div class="flex justify-center mb-8">
      <nav class="segmented-control" role="tablist">
        <button @click="activeTab = 'list'" :class="{ 'active': activeTab === 'list' }" role="tab">
          <span class="material-symbols-outlined">list_alt</span>
          Gastos
        </button>
        <button @click="activeTab = 'analytics'" :class="{ 'active': activeTab === 'analytics' }" role="tab">
          <span class="material-symbols-outlined">monitoring</span>
          Análise de Gastos
        </button>
      </nav>
    </div>

    <transition name="fade" mode="out-in">
      <div v-if="activeTab === 'list'" key="list">
        <div class="panel mb-6">
          <div class="flex flex-wrap items-center gap-4">
             <button @click="handleNovoGasto" class="btn btn-primary">
              <span class="material-symbols-outlined mr-2">add_circle</span>
              Adicionar Gasto
            </button>
            <button @click="exportToCSV" class="btn btn-secondary">
              <span class="material-symbols-outlined mr-2">download</span>
              Exportar Tabela
            </button>
          </div>
          <hr class="my-4 border-slate-200/80 dark:border-slate-700/60" />
          <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
            <select v-model="statusFiltro" class="form-select">
              <option value="">Status</option>
              <option value="PENDENTE">Pendente</option>
              <option value="PAGO">Pago</option>
              <option value="CONGELADO">Congelado</option>
            </select>
            <select v-model="categoriaFiltro" class="form-select">
              <option value="">Categoria</option>
              <option v-for="cat in categoriasDisponiveis" :key="cat" :value="cat">{{ cat }}</option>
            </select>
            <div class="flex gap-3 col-span-2">
              <select v-model="mesSelecionado" class="form-select w-full">
                <option value="">Mês</option>
                <option v-for="mes in meses" :key="mes.valor" :value="mes.valor">{{ mes.nome }}</option>
              </select>
              <select v-model="anoSelecionado" class="form-select w-full">
                <option value="">Ano</option>
                <option v-for="ano in anosDisponiveis" :key="ano" :value="ano">{{ ano }}</option>
              </select>
            </div>
          </div>
        </div>

        <div class="panel hidden sm:block">
          <div class="overflow-x-auto">
            <table class="w-full min-w-[800px] text-sm text-left">
              <thead class="text-xs text-slate-500 dark:text-slate-400 uppercase">
                <tr>
                  <th class="px-6 py-3 font-semibold">Descrição</th>
                  <th class="px-6 py-3 font-semibold text-center">Categoria</th>
                  <th class="px-6 py-3 font-semibold">Valor</th>
                  <th class="px-6 py-3 font-semibold">Vencimento</th>
                  <th class="px-6 py-3 font-semibold">Quitação</th>
                  <th class="px-6 py-3 font-semibold">Status</th>
                  <th class="px-6 py-3 font-semibold text-center">Ações</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-200/80 dark:divide-slate-700/60">
                <tr v-if="sortedGastos.length === 0">
                  <td colspan="7" class="empty-state h-48">
                    Nenhum gasto encontrado para os filtros selecionados.
                  </td>
                </tr>
                <tr v-for="gastoItem in sortedGastos" :key="gastoItem.id"
                  class="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                  <td class="px-6 py-4 font-medium text-slate-900 dark:text-white whitespace-nowrap truncate max-w-sm">
                    <div class="flex items-center gap-3">
                      <div class="w-2 h-8 rounded-full" :style="{ backgroundColor: getCategoryColor(gastoItem.categoria) }"></div>
                      <span class="truncate">{{ gastoItem.descricao }}</span>
                    </div>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-center text-slate-500 dark:text-slate-400">
                    <span :title="gastoItem.categoria" class="material-symbols-outlined text-lg">
                      {{ getCategoryIcon(gastoItem.categoria) }}
                    </span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap font-semibold text-slate-700 dark:text-slate-200">{{ formatCurrency(gastoItem.valor, false) }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-slate-500 dark:text-slate-400">{{ formatDate(gastoItem.dataPagamento) }}</td>
                  <td class="px-6 py-4 whitespace-nowrap text-slate-500 dark:text-slate-400">{{ formatDate(gastoItem.dataQuitacaoReal) }}</td>
                  <td class="px-6 py-4 whitespace-nowrap">
                    <select v-model="gastoItem.status" @change="updateStatus(gastoItem)" class="form-select-sm" :class="getStatusClass(gastoItem).text">
                      <option value="PENDENTE">Pendente</option>
                      <option value="PAGO">Pago</option>
                      <option value="CONGELADO">Congelado</option>
                    </select>
                  </td>
                  <td class="px-6 py-4 text-center whitespace-nowrap">
                    <div class="flex items-center justify-center gap-1">
                      <button @click="handleEditGasto(gastoItem)" title="Editar Gasto" class="btn-action-icon text-sky-600 dark:text-sky-400">
                        <span class="material-symbols-outlined text-lg">edit</span>
                      </button>
                      <button @click="confirmDelete(gastoItem.id)" title="Excluir Gasto" class="btn-action-icon text-rose-500 dark:text-rose-400">
                        <span class="material-symbols-outlined text-lg">delete</span>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
              <tfoot class="bg-slate-50 dark:bg-slate-900/50 font-semibold">
                <tr>
                  <td colspan="6" class="px-6 py-3 text-right text-slate-700 dark:text-slate-200">Total Pendente (Filtro):</td>
                  <td class="px-6 py-3 text-left text-slate-900 dark:text-white">{{ formatCurrency(totalGastosPendentes) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <div class="block sm:hidden space-y-4">
          <div v-if="sortedGastos.length === 0" class="empty-state mt-6">
            Nenhum gasto encontrado.
          </div>
          <div v-for="gastoItem in sortedGastos" :key="`card-${gastoItem.id}`" class="mobile-card" :style="{ borderLeftColor: getCategoryColor(gastoItem.categoria) }">
            <div class="flex justify-between items-start">
              <div class="flex-1 pr-4">
                <p class="font-bold text-slate-800 dark:text-slate-100">{{ gastoItem.descricao }}</p>
                <p class="text-sm text-slate-500 dark:text-slate-400 flex items-center gap-1">
                  <span class="material-symbols-outlined text-sm">{{ getCategoryIcon(gastoItem.categoria) }}</span>
                  {{ gastoItem.categoria || 'Sem Categoria' }}
                </p>
              </div>
              <p class="font-bold text-lg text-slate-900 dark:text-white">{{ formatCurrency(gastoItem.valor, false) }}</p>
            </div>
            <div class="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <div class="flex items-center gap-2">
                <span class="material-symbols-outlined text-base text-slate-500 dark:text-slate-400">event</span>
                <span class="text-slate-700 dark:text-slate-300">{{ formatDate(gastoItem.dataPagamento) }}</span>
              </div>
              <div v-if="gastoItem.status === 'PAGO' && gastoItem.dataQuitacaoReal" class="flex items-center gap-2">
                <span class="material-symbols-outlined text-base text-emerald-500">task_alt</span>
                <span class="text-slate-700 dark:text-slate-300">{{ formatDate(gastoItem.dataQuitacaoReal) }}</span>
              </div>
              <div class="col-span-2 flex items-center gap-2">
                <span class="material-symbols-outlined text-base" :class="getStatusClass(gastoItem).text">
                  {{ getStatusClass(gastoItem).icon }}
                </span>
                <select v-model="gastoItem.status" @change="updateStatus(gastoItem)" class="form-select-sm flex-1" :class="getStatusClass(gastoItem).text">
                  <option value="PENDENTE">Pendente</option>
                  <option value="PAGO">Pago</option>
                  <option value="CONGELADO">Congelado</option>
                </select>
              </div>
            </div>
            <div class="mt-3 flex justify-end">
              <div class="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-700/50 p-1">
                <button @click="handleEditGasto(gastoItem)" title="Editar Gasto" class="btn-action-icon text-sky-600 dark:text-sky-400"><span class="material-symbols-outlined text-lg">edit</span></button>
                <button @click="confirmDelete(gastoItem.id)" title="Excluir Gasto" class="btn-action-icon text-rose-500 dark:text-rose-400"><span class="material-symbols-outlined text-lg">delete</span></button>
              </div>
            </div>
          </div>
          <div v-if="sortedGastos.length > 0" class="panel mt-4 text-right font-semibold text-slate-800 dark:text-slate-100">
            Total Pendente: {{ formatCurrency(totalGastosPendentes) }}
          </div>
        </div>
      </div>
    </transition>

    <transition name="fade" mode="out-in">
       <div v-if="activeTab === 'analytics'" key="analytics">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div v-if="relatorioMensal?.gastosFixos" class="lg:col-span-2 panel">
            <h3 class="panel-title">Meta de Pagamento de Gastos Fixos</h3>
            <div class="flex justify-between items-baseline mb-1">
              <span class="text-sm font-medium text-slate-600 dark:text-slate-300">Progresso</span>
              <span class="text-sm font-bold" :class="relatorioMensal.gastosFixos.progressoPercentual >= 100 ? 'text-emerald-500' : 'text-[#5A189A] dark:text-[#D9BFFB]'">
                {{ relatorioMensal.gastosFixos.progressoPercentual.toFixed(0) }}%
              </span>
            </div>
            <div class="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-4">
              <div class="bg-[#5A189A] h-4 rounded-full transition-all duration-500" :style="{ width: relatorioMensal.gastosFixos.progressoPercentual + '%' }"></div>
            </div>
            <div class="text-xs text-slate-500 dark:text-slate-400 text-center mt-2">
              <span class="font-semibold text-emerald-600 dark:text-emerald-400">{{ formatCurrency(relatorioMensal.gastosFixos.valorPago) }}</span>
              de <span class="font-semibold text-slate-700 dark:text-slate-200">{{ formatCurrency(relatorioMensal.gastosFixos.valorTotal) }}</span>
            </div>
          </div>
          <div class="panel text-center">
            <h3 class="panel-title">Total Pendente (Filtro)</h3>
            <p class="text-4xl font-bold text-amber-500 dark:text-amber-400 my-4">{{ formatCurrency(totalGastosPendentes) }}</p>
          </div>
          <div class="panel text-center">
            <h3 class="panel-title">Total Pago (Filtro)</h3>
            <p class="text-4xl font-bold text-emerald-600 dark:text-emerald-400 my-4">{{ formatCurrency(totalGastosPagos) }}</p>
          </div>
          <div class="panel">
            <h3 class="panel-title">Comparativo Mensal</h3>
            <div v-if="comparativoMesAnterior.diferenca !== null" class="flex items-center" :class="comparativoMesAnterior.isIncrease ? 'text-red-500 dark:text-red-400' : 'text-green-500 dark:text-green-400'">
              <span class="material-symbols-outlined text-5xl">
                {{ comparativoMesAnterior.isIncrease ? 'arrow_upward' : 'arrow_downward' }}
              </span>
              <div class="ml-3">
                <p class="text-3xl font-bold">{{ formatCurrency(Math.abs(comparativoMesAnterior.diferenca)) }}</p>
                <p class="text-sm font-semibold">({{ comparativoMesAnterior.percentual.toFixed(1) }}%) {{ comparativoMesAnterior.isIncrease ? 'a mais' : 'a menos' }} que {{ comparativoMesAnterior.mesAnteriorNome }}</p>
              </div>
            </div>
            <div v-else class="empty-state h-full">
              <span class="material-symbols-outlined text-5xl">compare_arrows</span>
              <span>Sem dados para comparar.</span>
            </div>
          </div>
          <div class="panel">
            <h3 class="panel-title">Gastos por Categoria (Filtro)</h3>
            <div class="h-80">
              <Apexchart v-if="dadosGraficoCategoria.series[0]?.data.length > 0" type="bar" height="100%" :options="opcoesGraficoCategoria" :series="dadosGraficoCategoria.series"></Apexchart>
              <div v-else class="empty-state h-full">
                <span class="material-symbols-outlined text-5xl">bar_chart</span>
                <span>Sem dados para exibir.</span>
              </div>
            </div>
          </div>
          <div class="lg:col-span-2 panel">
            <h3 class="panel-title">Histórico de Gastos (Últimos 12 Meses)</h3>
            <div class="h-80">
              <Bar v-if="dadosGraficoHistorico.datasets[0].data.some(d => d > 0)" :data="dadosGraficoHistorico" :options="opcoesGraficoHistorico" />
              <div v-else class="empty-state h-full">
                <span class="material-symbols-outlined text-5xl">monitoring</span>
                <span>Dados insuficientes para o histórico.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <transition name="modal-fade">
      <div v-if="showModal" @click.self="closeModal" class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
        <div class="modal-panel max-w-md">
          <header class="modal-header">
            <h3 class="modal-title">
              {{ isEditing ? 'Editar Gasto' : 'Adicionar Novo Gasto' }}
            </h3>
            <button @click="closeModal" title="Fechar modal" :disabled="isSubmitting" class="btn-action-icon">
              <span class="material-symbols-outlined">close</span>
            </button>
          </header>
          <form @submit.prevent="handleSubmit" class="p-6 space-y-4">
            <div>
              <label class="form-label">Descrição</label>
              <input v-model="gasto.descricao" type="text" required placeholder="Ex: Conta de Luz" class="form-input" />
            </div>
            <div class="relative" ref="categoryDropdownContainer">
              <label class="form-label">Categoria</label>
              <button @click="isCategoryDropdownOpen = !isCategoryDropdownOpen" type="button" class="form-input flex items-center justify-between text-left">
                <span v-if="gasto.categoria" class="flex items-center gap-2">
                  <span class="material-symbols-outlined text-lg">{{ getCategoryIcon(gasto.categoria) }}</span>
                  <span>{{ gasto.categoria }}</span>
                </span>
                <span v-else class="text-slate-400 dark:text-slate-500">Selecione uma categoria</span>
                <span class="material-symbols-outlined text-slate-400">unfold_more</span>
              </button>
              <transition name="fade-fast">
                <div v-if="isCategoryDropdownOpen" class="absolute z-20 w-full mt-1 bg-white dark:bg-slate-900 rounded-lg shadow-lg border dark:border-slate-700 max-h-56 overflow-y-auto">
                  <ul>
                    <li v-for="(icon, name) in categoryIcons" :key="name" @click="selectCategory(name)" class="px-3 py-2 text-sm text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 cursor-pointer flex items-center gap-3">
                      <span class="material-symbols-outlined text-lg">{{ icon }}</span>
                      <span>{{ name }}</span>
                    </li>
                  </ul>
                </div>
              </transition>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="form-label">Valor (R$)</label>
                <input v-model.number="gasto.valor" type="number" step="0.01" required placeholder="0.00" class="form-input" />
              </div>
              <div>
                <label class="form-label">Data de Vencimento</label>
                <input v-model="gasto.dataPagamento" type="date" required class="form-input" />
              </div>
            </div>
            <div>
              <label class="form-label">Status</label>
              <select v-model="gasto.status" required class="form-select">
                <option value="PENDENTE">Pendente</option>
                <option value="PAGO">Pago</option>
                <option value="CONGELADO">Congelado</option>
              </select>
            </div>
            <footer class="pt-4 flex justify-end gap-3">
              <button @click="closeModal" type="button" :disabled="isSubmitting" class="btn btn-secondary">Cancelar</button>
              <button type="submit" :disabled="isSubmitting" class="btn btn-primary">
                <span v-if="isSubmitting" class="material-symbols-outlined animate-spin mr-2">progress_activity</span>
                {{ isSubmitting ? 'Salvando...' : (isEditing ? 'Atualizar' : 'Adicionar') }}
              </button>
            </footer>
          </form>
        </div>
      </div>
    </transition>
    
    <transition name="modal-fade">
      <div v-if="showDeleteConfirm" @click.self="showDeleteConfirm = false" class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
        <div class="modal-panel max-w-sm">
          <div class="p-8 text-center">
            <span class="material-symbols-outlined text-5xl text-rose-500 dark:text-rose-400 mx-auto">warning</span>
            <h3 class="mt-4 text-xl font-semibold">Excluir Gasto</h3>
            <p class="mt-2 text-sm text-slate-600 dark:text-slate-400">Tem certeza que deseja excluir este gasto?</p>
            <div class="mt-6 flex justify-center gap-3">
              <button @click="showDeleteConfirm = false" type="button" class="btn btn-secondary">Cancelar</button>
              <button @click="executeDelete" class="btn bg-rose-600 hover:bg-rose-700 text-white">Sim, Excluir</button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <transition name="modal-fade">
      <div v-if="showUnsavedChangesConfirm" @click.self="showUnsavedChangesConfirm = false" class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
        <div class="modal-panel max-w-md">
          <div class="p-8 text-center">
            <span class="material-symbols-outlined text-5xl text-amber-500 dark:text-amber-400 mx-auto">warning</span>
            <h3 class="mt-4 text-xl font-semibold">Descartar Alterações?</h3>
            <p class="mt-2 text-sm text-slate-600 dark:text-slate-400">
              Você possui alterações não salvas. Tem certeza que deseja sair?
            </p>
            <div class="mt-6 flex justify-center gap-3">
              <button @click="showUnsavedChangesConfirm = false" type="button" class="btn btn-secondary">Cancelar</button>
              <button @click="handleConfirmClose" class="btn bg-rose-600 hover:bg-rose-700 text-white">Sim, Descartar</button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch, onUnmounted, defineAsyncComponent } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import { Chart as ChartJS, Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale } from 'chart.js';

const Bar = defineAsyncComponent(() => import('vue-chartjs').then(module => module.Bar));
const Apexchart = defineAsyncComponent(() => import('vue3-apexcharts'));

ChartJS.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale);

const router = useRouter(); 
const allGastos = ref([]);
const showModal = ref(false);
const isEditing = ref(false);
const isSubmitting = ref(false);
const showDeleteConfirm = ref(false);
const itemParaDeletar = ref(null);
const isCategoryDropdownOpen = ref(false);
const categoryDropdownContainer = ref(null);
const activeTab = ref('list');
const relatorioMensal = ref(null);

const isMobile = ref(false);
const checkDevice = () => {
  isMobile.value = window.innerWidth < 640;
};

const getInitialGasto = () => ({
  id: null, descricao: '', valor: null, dataPagamento: '',
  status: 'PENDENTE', categoria: 'Padrão', dataQuitacaoReal: null
});

const gasto = ref(getInitialGasto());

const apiBaseUrl = 'https://backend.controlease.com.br';
const apiUrl = `${apiBaseUrl}/gastos`;
const relatorioApiUrl = `${apiBaseUrl}/relatorios/despesas-mensais`;

const mesSelecionado = ref('');
const anoSelecionado = ref('');
const statusFiltro = ref('');
const categoriaFiltro = ref('');

const pristineGasto = ref(null);
const showUnsavedChangesConfirm = ref(false);

const isDarkMode = computed(() => window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);

const isFormDirty = computed(() => {
  if (!pristineGasto.value) return false;
  return JSON.stringify(gasto.value) !== JSON.stringify(pristineGasto.value);
});

const meses = [
  { nome: 'Jan', valor: '01' }, { nome: 'Fev', valor: '02' }, { nome: 'Mar', valor: '03' },
  { nome: 'Abr', valor: '04' }, { nome: 'Mai', valor: '05' }, { nome: 'Jun', valor: '06' },
  { nome: 'Jul', valor: '07' }, { nome: 'Ago', valor: '08' }, { nome: 'Set', valor: '09' },
  { nome: 'Out', valor: '10' }, { nome: 'Nov', valor: '11' }, { nome: 'Dez', valor: '12' }
];

const categoryHexColors = {
  'Moradia': { light: '#3B82F6', dark: '#60A5FA' },
  'Saúde': { light: '#EF4444', dark: '#F87171' },
  'Alimentação': { light: '#22C55E', dark: '#4ADE80' },
  'Transporte': { light: '#F97316', dark: '#FB923C' },
  'Educação': { light: '#6366F1', dark: '#818CF8' },
  'Lazer': { light: '#14B8A6', dark: '#2DD4BF' },
  'Serviços': { light: '#06B6D4', dark: '#22D3EE' },
  'Compras': { light: '#EC4899', dark: '#F472B6' },
  'Viagem': { light: '#0EA5E9', dark: '#38BDF8' },
  'Padrão': { light: '#64748B', dark: '#94A3B8' }
};

const categoryIcons = {
  'Alimentação': 'restaurant', 'Transporte': 'commute', 'Moradia': 'home',
  'Lazer': 'sports_esports', 'Saúde': 'health_and_safety', 'Educação': 'school',
  'Compras': 'shopping_bag', 'Serviços': 'dns', 'Viagem': 'flight', 'Padrão': 'receipt_long'
};

const getCategoryIcon = (category) => categoryIcons[category] || categoryIcons['Padrão'];
const getCategoryColor = (category) => (categoryHexColors[category] || categoryHexColors['Padrão'])[isDarkMode.value ? 'dark' : 'light'];
const getMesAtual = () => String(new Date().getMonth() + 1).padStart(2, '0');
const getAnoAtual = () => String(new Date().getFullYear());

const authConfig = () => {
  const token = localStorage.getItem('token');
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};

const fetchGastos = async () => {
  try {
    const response = await axios.get(apiUrl, authConfig());
    allGastos.value = response.data;
  } catch (error) { console.error('Erro ao buscar gastos:', error); }
};

const fetchRelatorioMensal = async () => {
  if (!mesSelecionado.value || !anoSelecionado.value) {
    relatorioMensal.value = null;
    return;
  }
  try {
    const params = { mes: mesSelecionado.value, ano: anoSelecionado.value };
    const response = await axios.get(relatorioApiUrl, { ...authConfig(), params });
    relatorioMensal.value = response.data;
  } catch (error) {
    console.error('Erro ao buscar relatório mensal:', error);
    relatorioMensal.value = null;
  }
};

watch([mesSelecionado, anoSelecionado, activeTab], () => {
  if (activeTab.value === 'analytics') {
    fetchRelatorioMensal();
  }
}, { immediate: true });

const filteredGastos = computed(() => {
  return allGastos.value.filter(g => {
    if (!g.dataPagamento) return false;
    const [year, month] = g.dataPagamento.split('-');
    const mesOk = !mesSelecionado.value || month === mesSelecionado.value;
    const anoOk = !anoSelecionado.value || year === anoSelecionado.value;
    const statusOk = !statusFiltro.value || g.status === statusFiltro.value;
    const categoriaOk = !categoriaFiltro.value || g.categoria === categoriaFiltro.value;
    return mesOk && anoOk && statusOk && categoriaOk;
  });
});

const sortedGastos = computed(() => {
  return [...filteredGastos.value].sort((a, b) => new Date(a.dataPagamento) - new Date(b.dataPagamento));
});

const anosDisponiveis = computed(() => {
  const anos = new Set(allGastos.value.map(g => g.dataPagamento?.split('-')[0]).filter(Boolean));
  return Array.from(anos).sort((a, b) => parseInt(b, 10) - parseInt(a, 10));
});

const categoriasDisponiveis = computed(() => {
  const categorias = new Set(allGastos.value.map(g => g.categoria).filter(Boolean));
  return Array.from(categorias).sort();
});

const totalGastosPendentes = computed(() =>
  (filteredGastos.value || []).reduce((total, g) => {
    return g.status === 'PENDENTE' ? total + (parseFloat(g.valor) || 0) : total;
  }, 0)
);

const totalGastosPagos = computed(() =>
  (filteredGastos.value || []).reduce((total, g) => {
    return g.status === 'PAGO' ? total + (parseFloat(g.valor) || 0) : total;
  }, 0)
);

const gastosPorCategoria = computed(() => {
  const gastos = {};
  filteredGastos.value.forEach(g => {
    const categoria = g.categoria || 'Sem Categoria';
    gastos[categoria] = (gastos[categoria] || 0) + g.valor;
  });
  return Object.entries(gastos).sort(([, a], [, b]) => a - b);
});

const dadosGraficoCategoria = computed(() => ({
    series: [{ name: 'Gasto', data: gastosPorCategoria.value.map(([, valor]) => valor) }],
    labels: gastosPorCategoria.value.map(([nome]) => nome),
}));

const opcoesGraficoCategoria = computed(() => ({
    chart: { type: 'bar', background: 'transparent', toolbar: { show: false } },
    plotOptions: { bar: { borderRadius: 4, horizontal: true, distributed: true } },
    dataLabels: { enabled: false },
    colors: dadosGraficoCategoria.value.labels.map(nome => getCategoryColor(nome)),
    xaxis: { labels: { style: { colors: isDarkMode.value ? '#9CA3AF' : '#6B7280' } } },
    yaxis: { labels: { style: { colors: isDarkMode.value ? '#9CA3AF' : '#6B7280' } } },
    legend: { show: false },
    grid: { borderColor: isDarkMode.value ? '#374151' : '#E5E7EB' },
    tooltip: { theme: isDarkMode.value ? 'dark' : 'light', y: { formatter: (val) => formatCurrency(val) } }
}));

const dadosGraficoHistorico = computed(() => {
  const labels = [];
  const data = [];
  const hoje = new Date();
  for (let i = 11; i >= 0; i--) {
    const dataIteracao = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
    labels.push(dataIteracao.toLocaleString('pt-BR', { month: 'short', year: '2-digit' }));
    const ano = dataIteracao.getFullYear();
    const mes = String(dataIteracao.getMonth() + 1).padStart(2, '0');
    const totalMes = allGastos.value
      .filter(g => g.dataPagamento?.startsWith(`${ano}-${mes}`))
      .reduce((sum, g) => sum + g.valor, 0);
    data.push(totalMes);
  }
  return {
    labels,
    datasets: [{ label: 'Total Gasto', backgroundColor: '#EF4444', data, borderRadius: 4 }]
  };
});

const opcoesGraficoHistorico = computed(() => ({
    responsive: true, maintainAspectRatio: false,
    scales: {
        y: { grid: { color: isDarkMode.value ? 'rgba(148, 163, 184, 0.1)' : 'rgba(226, 232, 240, 1)' }, ticks: { color: isDarkMode.value ? '#94a3b8' : '#64748b' } },
        x: { grid: { display: false }, ticks: { color: isDarkMode.value ? '#94a3b8' : '#64748b' } }
    },
    plugins: {
        legend: { display: false },
        tooltip: { callbacks: { label: (ctx) => `${ctx.dataset.label}: ${formatCurrency(ctx.parsed.y)}` } }
    }
}));

const comparativoMesAnterior = computed(() => {
  const fallback = { diferenca: null, percentual: 0, isIncrease: false, mesAnteriorNome: '' };
  if (!mesSelecionado.value || !anoSelecionado.value) return fallback;
  const mesAtual = parseInt(mesSelecionado.value, 10);
  const anoAtual = parseInt(anoSelecionado.value, 10);
  let mesAnterior = mesAtual - 1, anoAnterior = anoAtual;
  if (mesAnterior === 0) { mesAnterior = 12; anoAnterior -= 1; }
  const mesAnteriorStr = String(mesAnterior).padStart(2, '0');
  const mesAnteriorNome = meses.find(m => m.valor === mesAnteriorStr)?.nome || '';
  const totalMesAtual = allGastos.value.filter(g => g.dataPagamento?.startsWith(`${anoAtual}-${mesSelecionado.value}`)).reduce((s, g) => s + g.valor, 0);
  const totalMesAnterior = allGastos.value.filter(g => g.dataPagamento?.startsWith(`${anoAnterior}-${mesAnteriorStr}`)).reduce((s, g) => s + g.valor, 0);
  if (totalMesAnterior === 0) return fallback;
  const diferenca = totalMesAtual - totalMesAnterior;
  const percentual = (diferenca / totalMesAnterior) * 100;
  return { diferenca, percentual, isIncrease: diferenca > 0, mesAnteriorNome: `${mesAnteriorNome}/${String(anoAnterior).slice(2)}` };
});

const exportToCSV = () => {
  if (sortedGastos.value.length === 0) return alert("Não há dados para exportar.");
  const headers = ["Descrição", "Categoria", "Valor", "Data Vencimento", "Data Quitação", "Status"];
  const formatCSVField = (field) => `"${String(field ?? '').replace(/"/g, '""')}"`;
  const rows = sortedGastos.value.map(g => [g.descricao, g.categoria, formatCurrency(g.valor, false).replace('.',','), formatDate(g.dataPagamento), formatDate(g.dataQuitacaoReal), g.status].map(formatCSVField).join(','));
  const csv = "\uFEFF" + headers.join(',') + '\n' + rows.join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = `gastos_fixos_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link); link.click(); document.body.removeChild(link);
};

const handleNovoGasto = () => {
  if (isMobile.value) {
    router.push({ name: 'GastoAdd' });
  } else {
    showAddGastoModal();
  }
};

const handleEditGasto = (gastoToEdit) => {
  if (isMobile.value) {
    router.push({ name: 'GastoEdit', params: { id: gastoToEdit.id } });
  } else {
    editGasto(gastoToEdit);
  }
};

const showAddGastoModal = () => {
  resetForm();
  pristineGasto.value = JSON.parse(JSON.stringify(gasto.value));
  showModal.value = true;
};

const handleSubmit = async () => {
  isSubmitting.value = true;
  try {
    if (isEditing.value) {
      await axios.put(`${apiUrl}/${gasto.value.id}`, gasto.value, authConfig());
    } else {
      await axios.post(apiUrl, gasto.value, authConfig());
    }
    await fetchGastos();
    if (activeTab.value === 'analytics') await fetchRelatorioMensal();
    forceCloseModal();
  } catch (error) {
    console.error('Erro ao salvar:', error); alert('Erro ao salvar.');
  } finally {
    isSubmitting.value = false;
  }
};

const editGasto = (gastoToEdit) => {
  gasto.value = { ...gastoToEdit };
  pristineGasto.value = { ...gastoToEdit };
  isEditing.value = true;
  showModal.value = true;
};


const confirmDelete = (id) => {
  itemParaDeletar.value = id;
  showDeleteConfirm.value = true;
};

const executeDelete = async () => {
  if (itemParaDeletar.value === null) return;
  try {
    await axios.delete(`${apiUrl}/${itemParaDeletar.value}`, authConfig());
    await fetchGastos();
    if (activeTab.value === 'analytics') await fetchRelatorioMensal();
  } catch (error) {
    console.error('Erro ao excluir:', error); alert('Erro ao excluir.');
  } finally {
    showDeleteConfirm.value = false;
    itemParaDeletar.value = null;
  }
};

const closeModal = () => {
  if (isSubmitting.value) return;
  if (isFormDirty.value) {
    showUnsavedChangesConfirm.value = true;
  } else {
    forceCloseModal();
  }
};

const forceCloseModal = () => { showModal.value = false; resetForm(); };
const handleConfirmClose = () => { showUnsavedChangesConfirm.value = false; forceCloseModal(); };

const updateStatus = async (gastoToUpdate) => {
  try {
    await axios.put(`${apiUrl}/${gastoToUpdate.id}`, gastoToUpdate, authConfig());
    await fetchGastos();
    if (activeTab.value === 'analytics') await fetchRelatorioMensal();
  } catch (error) {
    console.error('Erro ao atualizar status:', error);
    alert('Erro ao atualizar status.');
    await fetchGastos();
  }
};

const resetForm = () => {
  gasto.value = getInitialGasto();
  isEditing.value = false;
  pristineGasto.value = null;
};

const formatCurrency = (value, withSymbol = true) => {
    if (typeof value !== 'number') value = 0;
    const options = { minimumFractionDigits: 2, maximumFractionDigits: 2 };
    if (withSymbol) {
      options.style = 'currency';
      options.currency = 'BRL';
    }
    return value.toLocaleString('pt-BR', options);
}

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
}

const getStatusClass = (gastoItem) => {
  const todayStr = new Date().toISOString().split('T')[0];
  if (gastoItem.status === 'PENDENTE') {
    const isOverdue = gastoItem.dataPagamento < todayStr;
    return { text: isOverdue ? 'text-red-500 dark:text-red-400 font-semibold' : 'text-amber-600 dark:text-amber-400 font-semibold', icon: 'pending' };
  }
  if (gastoItem.status === 'PAGO') return { text: 'text-green-600 dark:text-green-400 font-semibold', icon: 'check_circle' };
  if (gastoItem.status === 'CONGELADO') return { text: 'text-sky-600 dark:text-sky-400', icon: 'ac_unit' };
  return { text: 'text-slate-600 dark:text-slate-300', icon: 'help' };
};

const selectCategory = (name) => {
  gasto.value.categoria = name;
  isCategoryDropdownOpen.value = false;
};

const handleClickOutside = (event) => {
  if (categoryDropdownContainer.value && !categoryDropdownContainer.value.contains(event.target)) {
    isCategoryDropdownOpen.value = false;
  }
};

watch(isCategoryDropdownOpen, (isOpen) => {
  if (isOpen) document.addEventListener('click', handleClickOutside);
  else document.removeEventListener('click', handleClickOutside);
});

onMounted(() => {
  mesSelecionado.value = getMesAtual();
  anoSelecionado.value = getAnoAtual();
  fetchGastos();
  fetchRelatorioMensal();
  checkDevice();
  window.addEventListener('resize', checkDevice);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  window.removeEventListener('resize', checkDevice);
});
</script>

<style>
.form-input,
.form-select {
  @apply w-full px-3 py-2 text-sm text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-700/50 border border-solid border-slate-300 dark:border-slate-600 rounded-lg transition ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500/70 focus:border-purple-500;
}
.form-select { @apply appearance-none py-2.5; }
.form-select-sm { @apply text-xs appearance-none block w-full px-2 py-1 font-normal bg-transparent border-0 focus:ring-0 focus:outline-none rounded-md cursor-pointer; }

.btn { @apply inline-flex items-center justify-center font-semibold py-2.5 px-5 rounded-xl transition-all duration-200 text-sm active:scale-95 disabled:opacity-60; }
.btn-primary { @apply btn bg-[#5A189A] hover:bg-[#3C096C] text-white shadow-lg shadow-[#5A189A]/20; }
.btn-secondary { @apply btn bg-white/70 dark:bg-slate-700/50 text-slate-700 dark:text-slate-200 hover:bg-slate-200/60 dark:hover:bg-slate-700 ring-1 ring-black/5 dark:ring-white/10 shadow-sm; }
.btn-action-icon { @apply h-8 w-8 grid place-items-center rounded-lg transition-colors text-slate-500 dark:text-slate-400 hover:bg-slate-200/70 dark:hover:bg-slate-700/50; }

.panel { @apply bg-white/70 dark:bg-[#240046]/60 backdrop-blur-lg ring-1 ring-black/5 dark:ring-white/10 rounded-2xl shadow-xl shadow-slate-200/30 dark:shadow-black/20; }
.panel-title { @apply text-lg font-semibold text-slate-700 dark:text-slate-200 mb-2; }

.segmented-control { @apply inline-flex rounded-2xl p-1 bg-slate-200/70 dark:bg-slate-800/80; }
.segmented-control button { @apply inline-flex items-center gap-2 px-4 sm:px-5 py-2.5 text-sm font-semibold rounded-xl transition-all; }
.segmented-control button.active { @apply bg-white dark:bg-slate-700 text-[#3C096C] dark:text-[#D9BFFB] shadow-md; }
.segmented-control button:not(.active) { @apply text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white; }

.mobile-card { @apply bg-white dark:bg-slate-800/60 p-4 rounded-lg shadow-lg border-l-4; }

.modal-panel { @apply bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl rounded-2xl shadow-2xl w-full ring-1 ring-black/10 dark:ring-white/10; }
.modal-header { @apply px-6 py-4 border-b border-slate-200/80 dark:border-slate-700/60 flex justify-between items-center; }
.modal-title { @apply text-lg font-semibold text-slate-800 dark:text-slate-100; }

.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.3s ease, transform 0.3s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; transform: scale(0.95) translateY(-20px); }
.fade-enter-active, .fade-leave-active { transition: opacity .3s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
.fade-fast-enter-active, .fade-fast-leave-active { transition: opacity 0.1s ease, transform 0.1s ease; }
.fade-fast-enter-from, .fade-fast-leave-to { opacity: 0; transform: scale(0.98) translateY(-4px); }

.animate-spin { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.empty-state { @apply flex flex-col items-center justify-center text-center text-slate-500 dark:text-slate-400 p-4 min-h-[100px]; }
</style>